import {Button, Choice, openDeleteDialog} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {CompositionFont, useDeleteCompositionFontMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getFontFaceLabel} from '../utils/getFontFaceLabel';
import {getFontFamilies} from '../utils/getFontFamilies';
import {BUILT_IN_FONT_OPTION_VALUE, getFontOptions} from '../utils/getFontOptions';
import {FontFacePatch} from '../utils/toFontFacePatch';
import {FontUpload} from './FontUpload';

interface FontPickerProps extends TestIdProps {
  // The layer's uploaded face, null for the built-in font.
  face: CompositionFont | null;
  fonts: CompositionFont[];
  isLoadingFonts: boolean;
  // `family` is null for the built-in font.
  onFamilyChange: (family: string | null) => void;
  onFontUploaded: (patch: FontFacePatch) => void;
  onFontDeleted: (fontId: string) => void;
}

export function FontPicker(props: FontPickerProps) {
  const [deleteCompositionFont] = useDeleteCompositionFontMutation();

  const familyOptionValue =
    getFontFamilies(props.fonts).find((family) => family.family === props.face?.family)
      ?.optionValue ?? BUILT_IN_FONT_OPTION_VALUE;

  const handleDelete = (face: CompositionFont) =>
    openDeleteDialog({
      text: i18n.t('entity.watermark.composition.labels.deleteFontConfirmation', {
        name: getFontFaceLabel(face),
      }),
      onConfirm: () =>
        deleteCompositionFont({id: face.id})
          .unwrap()
          .then(() => props.onFontDeleted(face.id))
          .catch(handleApiError),
    });

  return (
    <VStack spacing={2}>
      <HStack spacing={2} align="flex-end">
        <Box flex={1}>
          <Choice<string>
            label={i18n.t('entity.watermark.composition.labels.font')}
            value={familyOptionValue}
            options={getFontOptions(props.fonts)}
            isLoading={props.isLoadingFonts}
            isNotClearable
            onChange={(value) =>
              props.onFamilyChange(
                props.fonts.find((candidate) => candidate.id === value)?.family ?? null
              )
            }
            data-testid={suffixTestId('font', props)}
          />
        </Box>
        <Show when={props.face}>
          {(face) => (
            <Button
              variant="dangerLink"
              leftIcon="action/delete"
              title={i18n.t('general.actions.delete')}
              onClick={() => handleDelete(face)}
              data-testid={suffixTestId('deleteFont', props)}
            />
          )}
        </Show>
      </HStack>
      <FontUpload
        fonts={props.fonts}
        onUploaded={props.onFontUploaded}
        data-testid={suffixTestId('upload', props)}
      />
    </VStack>
  );
}
